import React, { useState, useEffect, useRef } from 'react';
import { Apiary } from '../types';
import './WeatherApiaryCard.css';

interface WeatherData {
  temperature: number;
  condition: 'sunny' | 'rain' | 'thunder' | 'wind' | 'snow';
  humidity: number;
  windSpeed: number;
  description: string;
  icon: string;
}

interface WeatherApiaryCardProps {
  apiary: Apiary;
  hiveCount: number;
  onEdit: (apiary: Apiary) => void;
  onDelete: (apiary: Apiary) => void;
  onInspect: (apiary: Apiary) => void;
  onViewOnMap: (apiary: Apiary) => void;
  onAddHive: (apiary: Apiary) => void;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  speed: number;
  delay: number;
  size?: number;
}

const WeatherApiaryCard: React.FC<WeatherApiaryCardProps> = ({
  apiary,
  hiveCount,
  onEdit,
  onDelete,
  onInspect,
  onViewOnMap,
  onAddHive
}) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [showLightning, setShowLightning] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const particleId = useRef(0);

  useEffect(() => {
    console.log(`Fetching weather for ${apiary.name} at ${apiary.lat}, ${apiary.lng}`);
    fetchWeatherData();
  }, [apiary.lat, apiary.lng]);

  useEffect(() => {
    if (weather) {
      generateParticles();
      if (weather.condition === 'thunder') {
        startLightningTimer();
      }
    }
    return () => {
      if (lightningTimerRef.current) {
        clearTimeout(lightningTimerRef.current);
      }
    };
  }, [weather]);

  const lightningTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startLightningTimer = () => {
    if (lightningTimerRef.current) clearTimeout(lightningTimerRef.current);
    if (weather?.condition === 'thunder') {
      lightningTimerRef.current = setTimeout(() => {
        triggerLightning();
        startLightningTimer(); // Continue the cycle
      }, Math.random() * 6000 + 2000); // 2-8 seconds
    }
  };

  const triggerLightning = () => {
    setShowLightning(true);
    setTimeout(() => setShowLightning(false), 200);
  };

  const generateParticles = () => {
    if (!weather) return;
    
    const newParticles: Particle[] = [];
    const particleCount = getParticleCount(weather.condition);
    
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        id: particleId.current++,
        x: Math.random() * 300,
        y: weather.condition === 'wind' ? 60 + Math.random() * 120 : Math.random() * -50, // Higher position for leaves
        speed: getParticleSpeed(weather.condition),
        delay: Math.random() * 2000,
        size: weather.condition === 'wind' ? 0.8 + Math.random() * 0.6 : 0.5 + Math.random() * 0.5 // Larger leaves
      });
    }
    
    setParticles(newParticles);
  };

  const getParticleCount = (condition: string) => {
    switch (condition) {
      case 'rain': return 15;
      case 'thunder': return 25;
      case 'snow': return 20;
      case 'wind': return 8;
      default: return 0;
    }
  };

  const getParticleSpeed = (condition: string) => {
    switch (condition) {
      case 'rain': return 1 + Math.random();
      case 'thunder': return 1.5 + Math.random();
      case 'snow': return 0.5 + Math.random() * 0.5;
      case 'wind': return 2 + Math.random();
      default: return 1;
    }
  };

  const fetchWeatherData = async () => {
    try {
      setLoading(true);
      
      // Using Open-Meteo API (free, no API key required)
      const apiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${apiary.lat}&longitude=${apiary.lng}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&timezone=auto`;
      console.log(`🌤️ [${apiary.name}] Fetching weather from:`, apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ [${apiary.name}] API Response:`, data);
        
        // Map Open-Meteo weather codes to our weather types
        const getWeatherCondition = (weatherCode: number): WeatherData['condition'] => {
          if (weatherCode >= 80 && weatherCode <= 99) return 'rain'; // Rain showers, thunderstorm
          if (weatherCode >= 95 && weatherCode <= 99) return 'thunder'; // Thunderstorm
          if (weatherCode >= 71 && weatherCode <= 77) return 'snow'; // Snow
          if (weatherCode >= 51 && weatherCode <= 67) return 'rain'; // Drizzle, rain
          if (weatherCode === 0) return 'sunny'; // Clear sky
          if (weatherCode >= 1 && weatherCode <= 3) return 'sunny'; // Mainly clear, partly cloudy
          return 'sunny'; // Default
        };

        const getWeatherDescription = (weatherCode: number): string => {
          const descriptions: { [key: number]: string } = {
            0: 'clear sky',
            1: 'mainly clear',
            2: 'partly cloudy',
            3: 'overcast',
            45: 'fog',
            48: 'depositing rime fog',
            51: 'light drizzle',
            53: 'moderate drizzle',
            55: 'dense drizzle',
            61: 'slight rain',
            63: 'moderate rain',
            65: 'heavy rain',
            71: 'slight snow',
            73: 'moderate snow',
            75: 'heavy snow',
            80: 'slight rain showers',
            81: 'moderate rain showers',
            82: 'violent rain showers',
            95: 'thunderstorm',
            96: 'thunderstorm with slight hail',
            99: 'thunderstorm with heavy hail'
          };
          return descriptions[weatherCode] || 'partly cloudy';
        };

        const weatherCode = data.current.weather_code;
        const condition = getWeatherCondition(weatherCode);
        
        // Check if it's windy based on wind speed (15 m/s = ~33 mph threshold)
        const finalCondition = data.current.wind_speed_10m > 15 ? 'wind' : condition;

        setWeather({
          temperature: Math.round(data.current.temperature_2m),
          condition: finalCondition,
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m * 10) / 10,
          description: getWeatherDescription(weatherCode),
          icon: weatherCode === 0 ? '01d' : weatherCode <= 3 ? '02d' : '03d'
        });
        console.log(`✅ [${apiary.name}] Real weather set: ${Math.round(data.current.temperature_2m)}°C`);
        return;
      } else {
        console.warn(`❌ [${apiary.name}] API response not ok:`, response.status, response.statusText);
      }
      
      console.log(`🎲 [${apiary.name}] Using mock weather data (API failed or not ok)`);
      
      // Generate varied mock weather data based on apiary location and name
      const generateLocationBasedWeather = () => {
        console.log(`🎲 [${apiary.name}] Generating mock weather based on location hash`);
        const apiaryHash = (apiary.name + apiary.lat + apiary.lng).split('').reduce((a, b) => {
          a = ((a << 5) - a) + b.charCodeAt(0);
          return a & a;
        }, 0);
        
        const conditions: WeatherData['condition'][] = ['sunny', 'rain', 'thunder', 'wind'];
        const selectedCondition = conditions[Math.abs(apiaryHash) % conditions.length];
        
        const baseTemp = 15 + (Math.abs(apiaryHash) % 15); // 15-30°C
        const humidity = 45 + (Math.abs(apiaryHash) % 40); // 45-85%
        const windSpeed = 1 + (Math.abs(apiaryHash) % 8); // 1-9 m/s
        
        const descriptions = {
          sunny: ['clear sky', 'few clouds', 'scattered clouds'],
          rain: ['light rain', 'moderate rain', 'heavy rain'],
          thunder: ['thunderstorm', 'thunderstorm with rain', 'severe thunderstorm'],
          wind: ['windy', 'breezy', 'strong winds'],
          snow: ['light snow', 'snow', 'heavy snow']
        };
        
        const conditionDescriptions = descriptions[selectedCondition];
        const description = conditionDescriptions[Math.abs(apiaryHash) % conditionDescriptions.length];
        
        const mockWeather = {
          temperature: baseTemp,
          condition: selectedCondition,
          humidity,
          windSpeed: Math.round(windSpeed * 10) / 10,
          description,
          icon: selectedCondition === 'sunny' ? '01d' : selectedCondition === 'rain' ? '10d' : '03d'
        };
        
        console.log(`🎲 [${apiary.name}] Generated mock weather:`, mockWeather);
        return mockWeather;
      };
      
      setWeather(generateLocationBasedWeather());
      
    } catch (error) {
      console.error(`❌ [${apiary.name}] Error fetching weather:`, error);
      console.log(`🎲 [${apiary.name}] Using fallback weather data due to error`);
      // Generate varied fallback data instead of same data for all
      const fallbackWeather = {
        temperature: 15 + Math.floor(Math.random() * 10),
        condition: (['sunny', 'rain', 'wind'] as const)[Math.floor(Math.random() * 3)],
        humidity: 50 + Math.floor(Math.random() * 30),
        windSpeed: Math.round((1 + Math.random() * 5) * 10) / 10,
        description: 'partly cloudy',
        icon: '02d'
      };
      setWeather(fallbackWeather);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="apiary-card-container">
      <div className={`animated-weather-card ${weather?.condition || 'sunny'}`} ref={cardRef}>
        {/* Weather Card Background */}
        <div id="card" className="weather">
          {/* SVG Background for Weather Effects */}
          <svg id="inner" width="100%" height="100%">
            <defs>
              <radialGradient id="sun-gradient" cx="50%" cy="20%" r="40%">
                <stop offset="0%" stopColor="#F7ED47" />
                <stop offset="100%" stopColor="#FCD658" />
              </radialGradient>
              <linearGradient id="cloud-gradient-1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#f0f0f0" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            
            {/* Animated Sun (for sunny weather) */}
            {weather?.condition === 'sunny' && (
              <>
                <circle 
                  id="sun" 
                  cx="150" 
                  cy="80" 
                  r="35" 
                  fill="url(#sun-gradient)"
                  style={{
                    animation: 'rotate-sun 20s linear infinite',
                    transformOrigin: '150px 80px'
                  }}
                />
                {/* Sun rays */}
                <g stroke="#F7ED47" strokeWidth="3" strokeLinecap="round" opacity="0.7">
                  <line x1="150" y1="25" x2="150" y2="35" />
                  <line x1="150" y1="125" x2="150" y2="135" />
                  <line x1="95" y1="80" x2="85" y2="80" />
                  <line x1="205" y1="80" x2="215" y2="80" />
                  <line x1="115" y1="45" x2="110" y2="40" />
                  <line x1="185" y1="115" x2="190" y2="120" />
                  <line x1="185" y1="45" x2="190" y2="40" />
                  <line x1="115" y1="115" x2="110" y2="120" />
                </g>
              </>
            )}
            
            {/* Animated Clouds with natural rounded shapes */}
            <g id="cloud3" className="cloud">
              <path 
                d="M-100,120 Q-80,105 -60,110 Q-40,95 -20,110 Q0,100 20,115 Q40,105 60,120 Q80,110 100,125 Q120,115 140,130 Q160,120 180,135 Q200,125 220,140 Q240,130 260,145 Q280,135 300,150 Q320,140 340,155 Q360,145 380,160 Q400,150 420,165 Q440,155 460,170 L460,180 Q440,175 420,180 Q400,185 380,180 Q360,185 340,180 Q320,185 300,180 Q280,185 260,180 Q240,185 220,180 Q200,185 180,180 Q160,185 140,180 Q120,185 100,180 Q80,185 60,180 Q40,185 20,180 Q0,185 -20,180 Q-40,185 -60,180 Q-80,185 -100,180 Z" 
                fill={weather?.condition === 'thunder' ? '#7B7988' : '#D5D5D5'}
                style={{
                  animation: 'float-cloud-3 35s linear infinite'
                }}
              />
            </g>
            <g id="cloud2" className="cloud">
              <path 
                d="M-120,80 Q-100,65 -80,70 Q-60,55 -40,70 Q-20,60 0,75 Q20,65 40,80 Q60,70 80,85 Q100,75 120,90 Q140,80 160,95 Q180,85 200,100 Q220,90 240,105 Q260,95 280,110 Q300,100 320,115 Q340,105 360,120 Q380,110 400,125 Q420,115 440,130 L440,140 Q420,135 400,140 Q380,145 360,140 Q340,145 320,140 Q300,145 280,140 Q260,145 240,140 Q220,145 200,140 Q180,145 160,140 Q140,145 120,140 Q100,145 80,140 Q60,145 40,140 Q20,145 0,140 Q-20,145 -40,140 Q-60,145 -80,140 Q-100,145 -120,140 Z" 
                fill={weather?.condition === 'thunder' ? '#8B8E98' : '#E6E6E6'}
                style={{
                  animation: 'float-cloud-2 30s linear infinite'
                }}
              />
            </g>
            <g id="cloud1" className="cloud">
              <path 
                d="M-140,40 Q-120,25 -100,30 Q-80,15 -60,30 Q-40,20 -20,35 Q0,25 20,40 Q40,30 60,45 Q80,35 100,50 Q120,40 140,55 Q160,45 180,60 Q200,50 220,65 Q240,55 260,70 Q280,60 300,75 Q320,65 340,80 Q360,70 380,85 Q400,75 420,90 Q440,80 460,95 L460,105 Q440,100 420,105 Q400,110 380,105 Q360,110 340,105 Q320,110 300,105 Q280,110 260,105 Q240,110 220,105 Q200,110 180,105 Q160,110 140,105 Q120,110 100,105 Q80,110 60,105 Q40,110 20,105 Q0,110 -20,105 Q-40,110 -60,105 Q-80,110 -100,105 Q-120,110 -140,105 Z" 
                fill={weather?.condition === 'thunder' ? '#9FA4AD' : '#efefef'}
                style={{
                  animation: 'float-cloud-1 25s linear infinite'
                }}
              />
            </g>

            {/* Lightning bolt for thunder */}
            {weather?.condition === 'thunder' && showLightning && (
              <g opacity="1">
                <path 
                  d="M150,20 L140,60 L155,60 L145,100 L160,60 L150,60 Z" 
                  fill="white" 
                  stroke="white" 
                  strokeWidth="2"
                  style={{
                    animation: 'lightning-bolt 0.2s ease-out'
                  }}
                />
              </g>
            )}
          </svg>

          {/* Lightning flash overlay */}
          {weather?.condition === 'thunder' && showLightning && (
            <div 
              className="lightning-flash"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.9)',
                animation: 'lightning-flash 0.2s ease-out',
                pointerEvents: 'none'
              }}
            />
          )}

          {/* Dynamic Weather Particles */}
          <div className="weather-particles">
            {particles.map((particle) => (
              <div
                key={particle.id}
                className={`weather-particle ${getParticleClass(weather?.condition || 'sunny')}`}
                style={{
                  left: `${particle.x}px`,
                  top: `${particle.y}px`,
                  animationDelay: `${particle.delay}ms`,
                  animationDuration: `${2 / particle.speed}s`,
                  transform: `scale(${particle.size})`,
                  position: 'absolute'
                }}
              />
            ))}
          </div>

          {/* Weather Details Overlay */}
          <div className="details">
            <div className="temp">
              {loading ? '...' : weather?.temperature || 18}
              <span>°C</span>
            </div>
            <div className="right">
              <div id="date">{getCurrentDate()}</div>
              <div id="summary">{weather?.description || 'Loading...'}</div>
            </div>
          </div>
        </div>

        {/* Apiary Information Overlay */}
        <div className="apiary-overlay">
          <div className="apiary-header">
            <h3>{apiary.name}</h3>
            <span className="hive-count">{hiveCount} hives</span>
          </div>
          
          <div className="apiary-details">
            <p><i className="fas fa-map-marker-alt"></i> {apiary.location}</p>
            <p><i className="fas fa-globe"></i> {apiary.lat.toFixed(4)}, {apiary.lng.toFixed(4)}</p>
            
            {weather && !loading && (
              <div className="weather-stats">
                <span><i className="fas fa-tint"></i> {weather.humidity}%</span>
                <span><i className="fas fa-wind"></i> {weather.windSpeed.toFixed(1)} m/s</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="card-actions">
            <div className="primary-actions">
              <button 
                className="primary-btn inspect-btn"
                onClick={() => onInspect(apiary)}
                title="Start inspection"
              >
                <i className="fas fa-search"></i> INSPECT
              </button>
              <button 
                className="primary-btn map-btn"
                onClick={() => onViewOnMap(apiary)}
                title="View on map"
              >
                <i className="fas fa-map-marked-alt"></i> MAP VIEW
              </button>
            </div>
            <div className="secondary-actions">
              <button 
                className="icon-btn add-btn"
                onClick={() => onAddHive(apiary)}
                title="Add new hive"
              >
                <i className="fas fa-plus"></i>
              </button>
              <button 
                className="icon-btn edit-btn"
                onClick={() => onEdit(apiary)}
                title="Edit apiary"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button 
                className="icon-btn delete-btn"
                onClick={() => onDelete(apiary)}
                title="Delete apiary"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>

          {/* Weather-based Beekeeping Tips */}
          {weather && !loading && (
            <div className="beekeeping-tip">
              <small>
                <i className="fas fa-lightbulb"></i> {getBeekeepingTip(weather.condition, weather.temperature, weather.windSpeed)}
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to get particle CSS class based on weather
const getParticleClass = (condition: string): string => {
  switch (condition) {
    case 'rain':
    case 'thunder':
      return 'rain-drop';
    case 'snow':
      return 'snow-flake';
    case 'wind':
      return 'wind-leaf';
    default:
      return '';
  }
};

// Helper function for weather-based beekeeping tips
const getBeekeepingTip = (condition: string, temp: number, windSpeed: number): string => {
  if (temp < 10) return "Too cold for inspections - bees are clustered for warmth";
  if (temp > 30) return "Hot day - provide shade and check water sources";
  if (condition === 'rain') return "Avoid opening hives - bees are sheltering inside";
  if (condition === 'thunder') return "Storm weather - postpone any hive work";
  if (windSpeed > 15) return "Windy conditions - bees may be defensive";
  if (condition === 'sunny' && temp > 15) return "Perfect weather for hive inspections!";
  return "Check local conditions before opening hives";
};

export default WeatherApiaryCard;
